/* eslint-disable @typescript-eslint/ban-types */
import React, {PureComponent} from 'react';
import {Tab, Tabs} from 'react-bootstrap';
import classNames from 'classnames';

import EmojiIcon from 'components/widgets/icons/emoji_icon';
import GfycatIcon from 'components/widgets/icons/gfycat_icon';
import {makeAsyncComponent} from 'components/async_load';
import EmojiPicker from 'components/emoji_picker';
import EmojiPickerHeader from 'components/emoji_picker/components/emoji_picker_header';
import {type Emoji} from '@mattermost/types/emojis';

const GifPicker = makeAsyncComponent(
    'GifPicker',
    React.lazy(() =>
        import(/* webpackChunkName: "components-gif_picker-gif_picker"*/ 'components/gif_picker/gif_picker.jsx')),
);

type Props = {
    className?: string;
    style?: React.CSSProperties;
    rightOffset?: number;
    topOffset?: number;
    leftOffset?: number;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    customEmojis?: Record<string, any>;
    onEmojiClose: Function;
    onEmojiClick: (emoji: Emoji) => void;
    onGifClick?: Function;
    enableGifPicker?: boolean;
};

type State = {
    emojiTabVisible: boolean;
    filter: string;
}

export default class EmojiPickerTabs extends PureComponent<Props, State> {
    static defaultProps = {
        rightOffset: 0,
        topOffset: 0,
        leftOffset: 0,
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            emojiTabVisible: true,
            filter: '',
        };
    }

    handleEnterEmojiTab = () => {
        this.setState({
            emojiTabVisible: true,
        });
    };

    handleExitEmojiTab = () => {
        this.setState({
            emojiTabVisible: false,
        });
    };

    handleEmojiPickerClose = () => {
        this.props.onEmojiClose();
    };

    handleFilterChange = (filter: string) => {
        this.setState({filter});
    };

    render() {
        let pickerStyle;
        const {
            onEmojiClick,
            enableGifPicker,
            rightOffset,
            onGifClick,
            style,
            className,
            leftOffset,
            topOffset,
            placement,
        } = this.props;
        if (style && !(style.left === 0 && style.top === 0)) {
            if (placement === 'top' || placement === 'bottom') {
                // Only take the top/bottom position passed by React Bootstrap since we want to be right-aligned
                pickerStyle = {
                    top: style.top,
                    bottom: style.bottom,
                    right: rightOffset,
                };
            } else {
                pickerStyle = {...style};
            }

            pickerStyle.top = pickerStyle.top ? pickerStyle.top + topOffset : topOffset;

            if (pickerStyle.left) {
                pickerStyle.left += leftOffset;
            }
        }

        let pickerClass = 'emoji-picker';
        if (placement === 'bottom') {
            pickerClass += ' bottom';
        }

        if (enableGifPicker && typeof onGifClick != 'undefined') {
            return (
                <Tabs
                    defaultActiveKey={1}
                    id='emoji-picker-tabs'
                    style={pickerStyle}
                    className={pickerClass}
                    justified={true}
                >
                    <EmojiPickerHeader handleEmojiPickerClose={this.handleEmojiPickerClose} />
                    <Tab
                        eventKey={1}
                        onEnter={this.handleEnterEmojiTab}
                        onExit={this.handleExitEmojiTab}
                        title={
                            <div className={'custom-emoji-tab__icon__text'}>
                                <EmojiIcon className='custom-emoji-tab__icon' />
                                <div>{'Emojis'}</div>
                            </div>
                        }
                        tabClassName={'custom-emoji-tab'}
                    >
                        <EmojiPicker
                            filter={this.state.filter}
                            visible={this.state.emojiTabVisible}
                            onEmojiClick={onEmojiClick}
                            handleFilterChange={this.handleFilterChange}
                        />
                    </Tab>
                    <Tab
                        eventKey={2}
                        title={<GfycatIcon />}
                        unmountOnExit={true}
                        tabClassName={'custom-emoji-tab'}
                    >
                        <GifPicker
                            onGifClick={onGifClick}
                            defaultSearchText={this.state.filter}
                            handleSearchTextChange={this.handleFilterChange}
                        />
                    </Tab>
                </Tabs>
            );
        }

        return (
            <div
                id='emojiPicker'
                style={pickerStyle}
                className={classNames('a11y__popup', pickerClass, 'emoji-picker--single', className)}
            >
                <EmojiPickerHeader handleEmojiPickerClose={this.handleEmojiPickerClose} />
                <EmojiPicker
                    filter={this.state.filter}
                    visible={this.state.emojiTabVisible}
                    onEmojiClick={onEmojiClick}
                    handleFilterChange={this.handleFilterChange}
                />
            </div>
        );
    }
}
